(ns ui.sticker-dialogs.location
  "The Replicant location sticker dialog: search Instagram locations
   (near the browser's position when allowed) or pick a recent one.
   With its `:sticker-dialog/location-*` action handlers."
  (:require [cljs.core.async :refer [<!]]
            [parts.replicant.query :as query]
            [storrito.editor.sticker-design :as sticker-design]
            [storrito.instagram.story.editor.location-search :as location-search]
            [ui.sticker-dialogs.actions :as actions]
            [ui.sticker-dialogs.recent :as recent]
            [ui.sticker-dialogs.storage :as storage]
            [ui.sticker-dialogs.ui :as ui])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(def ig-account-query
  {:query/kind :query/ig-account-data})

(def input-id
  "sticker-location-input")

(defn initial-state
  [_w]
  {:query ""
   :recent (storage/recent-locations)})

(defn browser-location!
  "Asks the browser for the position once the location dialog opened,
   so the search can be sorted by distance. Denied or unsupported: the
   search works with the query text alone."
  [{:keys [store]}]
  (if (.-geolocation js/navigator)
    (.getCurrentPosition (.-geolocation js/navigator)
                         (fn [position]
                           (actions/swap-dialog-of! store :location assoc
                                                    :browser-location [(.-latitude (.-coords position))
                                                                       (.-longitude (.-coords position))]))
                         (fn [_error]
                           (actions/swap-dialog-of! store :location assoc :browser-location false)))
    (actions/swap-dialog-of! store :location assoc :browser-location false)))

(defn search!
  [{:keys [store]}]
  (let [dialog (actions/dialog @store)
        [lat lng] (or (:browser-location dialog) [0 0])]
    (actions/swap-dialog! store (fn [dialog]
                                  (-> dialog
                                      (assoc :searching? true)
                                      (dissoc :locations :error-message))))
    (go
      (let [result (<! (location-search/search-locations {:lat lat
                                                          :lng lng
                                                          :query (:query dialog)}))]
        (actions/swap-dialog-of!
          store
          :location
          (fn [dialog]
            (-> dialog
                (dissoc :searching?)
                (merge (if (instance? js/Error result)
                         {:error-message "The search failed. Please try again."}
                         result)))))))))

(defn select!
  "A search result or a recent location becomes the location sticker."
  [{:keys [store ui/event-handler action]}]
  (let [[_ location] action
        {:keys [existing]} (actions/dialog @store)
        data (:asset/data existing)]
    (storage/remember-location! location)
    (event-handler {}
                   (concat (actions/asset-actions
                             existing
                             (actions/asset-tx existing
                                               :location-sticker
                                               "Location Sticker"
                                               (actions/invisible
                                                 {:location location
                                                  :design (or (:design data)
                                                              (first sticker-design/new-location-designs))}
                                                 (:invisible data))))
                           [[:ui/modal-close]]))))

(defn forget!
  [{:keys [store action]}]
  (let [[_ location] action]
    (storage/forget-location! location)
    (actions/swap-dialog! store assoc :recent (storage/recent-locations))))

(defn location-list
  [{:keys [locations]}]
  [:div.h-full.overflow-auto
   {:style {:max-height "40vh"}}
   [:table
    [:tbody
     (map-indexed (fn [idx location]
                    [:tr.cursor-pointer.hover:bg-gray-100
                     {:replicant/key idx
                      :title (str "(lat, lng): (" (:lat location) ", " (:lng location) ")")
                      :on {:click [[:sticker-dialog/location-select location]]}}
                     [:td.p-2.text-lg [:i.fas.fa-map-marker-alt]]
                     [:td.p-2
                      (:name location)
                      [:br]
                      [:small.text-gray-600 (:address location)]]])
                  locations)]]])

(defn recent-chips
  [{:keys [recent]}]
  (when (seq recent)
    [:div.mt-1
     [:div.flex.flex-wrap.gap-2
      (map (fn [{:keys [name address] :as location}]
             [:span {:replicant/key (recent/location-key location)}
              (ui/chip {:label name
                        :sublabel address
                        :hover-color "purple"
                        :title (str "Add " name)
                        :actions [[:sticker-dialog/location-select location]]
                        :remove-actions [[:sticker-dialog/location-forget location]]
                        :remove-title (str "Remove " name " from saved locations")})])
           recent)]
     [:p.mt-2.text-xs.text-gray-500
      "If a location changed, remove the saved location with × and search again."]]))

(defn search-panel
  [{:keys [searching? query browser-location error-message] :as dialog}]
  [:div.gap-4.mt-4.flex.flex-col
   {:replicant/on-mount [[:sticker-dialog/location-geolocate]]}
   [:form.m-0
    {:on {:submit [[:js/prevent-default]
                   [:sticker-dialog/location-search]]}}
    [:label.text-gray-800.block.text-base.font-bold.mb-2 "Enter a Location:"]
    [:div.flex
     (ui/text-field dialog
                    {:field :query
                     :dom-id input-id
                     :autofocus? true
                     :class ["mr-2" "max-w-full" "w-full" "bg-white" "border-gray-400" "rounded"
                             "text-gray-800" "shadow-sm" "focus:hover:border-gray-500" "focus:shadow-md"]})
     (ui/primary-button {:type "submit"
                         :class (concat ui/primary-css ["ml-2"])
                         :disabled (or searching?
                                       (and (ui/blank? query)
                                            (not browser-location)))}
                        (if searching?
                          [:span [:i.fas.fa-spinner.fa-spin.mr-2] "Searching..."]
                          [:span [:i.fas.fa-search.mr-2] "Search"]))]
    (ui/error-text error-message)]
   (recent-chips dialog)
   (location-list dialog)])

(defn panel
  [state dialog]
  (let [result (query/get-result state ig-account-query)
        connected? (boolean (seq (get-in result [:instagram-data :instagram.accounts])))]
    [:div.flex.flex-col
     (ui/title [:span [:i.fa-solid.fa-location-dot.mr-2] "LOCATION"]
               {:color "rgb(161, 0, 178)"})
     (cond
       (and (nil? result)
            (query/loading? state ig-account-query))
       [:span "loading..."]

       connected?
       (search-panel dialog)

       :else
       (ui/need-to-connect))]))

(defn render
  [state]
  (when-let [dialog (actions/dialog state)]
    (panel state dialog)))

(def modal
  {:modal-id :modals/editor2-location-modal
   :on-load (fn [_state]
              [[:data/query ig-account-query]
               [:sticker-dialog/init :location]])
   :sticker/kind :location
   :sticker/single-asset-type :location-sticker
   :sticker/initial-state #'initial-state
   :render #'render})

(def register
  [modal
   (actions/handler :sticker-dialog/location-geolocate browser-location!)
   (actions/handler :sticker-dialog/location-search search!)
   (actions/handler :sticker-dialog/location-select select!)
   (actions/handler :sticker-dialog/location-forget forget!)])
